'use client'

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from '@/shared/navigation'
import {
  fetchKitchenTransferTrips,
  fetchKitchenTransferTrip,
  generateKitchenTransferQr,
  isKitchenTransferQrExpired,
  formatTransferLineSummary,
  formatTransferLineLabel,
  type ApiKitchenTransferDrop,
  type ApiKitchenTransferTrip,
  type ApiKitchenTransferLine,
} from '@/features/delman/api/delmanKitchenTransferApi'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { DelmanPageHeader } from '@/features/delman/components/DelmanPageHeader'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { QrDisplay } from '@/shared/components/QrDisplay'
import { Toast } from '@/shared/components/Toast'
import { todayDateKey } from '@/features/kitchen/storage/attendanceStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'
import { qrCountdownLabel } from '@/shared/utils/qrCountdown'
import { buildKitchenTransferHandoverQrPayload } from '@/shared/utils/qrHandoverPayload'

function dropStatusBadge(status: string): string {
  if (status === 'receiving_completed') return 'badge-success'
  if (status === 'receiving_draft' || status === 'qr_shown') return 'badge-warning'
  return 'badge-draft'
}

function dropStatusLabel(status: string): string {
  if (status === 'receiving_completed') return delmanTerms.kitchenTransfer.dropDone
  if (status === 'receiving_draft') return delmanTerms.kitchenTransfer.dropScanned
  return delmanTerms.kitchenTransfer.dropPending
}

export function DelmanKitchenTransferListPage() {
  const hydrated = useClientHydrated()
  const deviceId = hydrated ? getDelmanDevice()?.deviceId ?? null : null
  const [trips, setTrips] = useState<ApiKitchenTransferTrip[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!deviceId) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    void fetchKitchenTransferTrips(todayDateKey())
      .then((items) => {
        if (!cancelled) setTrips(items)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [deviceId])

  return (
    <section className="space-y-4">
      <div className="card">
        <p className="text-headline-md text-primary">{delmanTerms.modules.kitchenTransfer}</p>
        <p className="text-body-sm text-on-surface-variant mt-1">
          Kiriman dari dapur lain — tampilkan QR untuk dapur tujuan scan.
        </p>
      </div>

      {loading && <SkeletonList count={2} label="Memuat trip antar dapur" />}

      {!loading && trips.length === 0 && (
        <EmptyState
          icon="swap_horiz"
          title="Belum ada trip antar dapur"
          description={delmanTerms.kitchenTransfer.empty}
        />
      )}

      {!loading &&
        trips.map((trip) => (
          <div key={trip.id} className="card space-y-3">
            <div>
              <p className="text-label-md font-mono text-on-surface-variant">{trip.trip_code}</p>
              <p className="text-body-lg font-semibold text-on-surface mt-1">
                Dari {trip.source_kitchen_name}
              </p>
            </div>
            <div className="space-y-2">
              {trip.transfers.map((drop) => (
                <Link
                  key={drop.id}
                  href={`/delman/kitchen-transfer/${drop.id}?tripId=${trip.id}`}
                  className="block rounded-lg border border-outline-variant p-3 hover:border-primary transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-sm font-semibold text-on-surface">
                        → {drop.target_kitchen_name}
                      </p>
                      <p className="text-label-md text-on-surface-variant mt-1">
                        {formatTransferLineSummary(drop.lines)}
                      </p>
                    </div>
                    <span className={dropStatusBadge(drop.status)}>{dropStatusLabel(drop.status)}</span>
                  </div>
                  <p className="text-body-sm text-primary font-semibold mt-2">
                    {drop.status === 'receiving_completed'
                      ? delmanTerms.kitchenTransfer.dropDone
                      : delmanTerms.kitchenTransfer.dropAction}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        ))}
    </section>
  )
}

export function DelmanKitchenTransferHandoverPage() {
  const { transferId } = useParams<{ transferId: string }>()
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const [drop, setDrop] = useState<ApiKitchenTransferDrop | null>(null)
  const [token, setToken] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [now, setNow] = useState(0)

  useEffect(() => {
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const loadDrop = useCallback(async () => {
    if (!transferId || typeof window === 'undefined') return
    const tripId = new URLSearchParams(window.location.search).get('tripId')
    if (!tripId) return
    const trip = await fetchKitchenTransferTrip(tripId)
    const found = trip.transfers.find((t) => t.id === transferId) ?? null
    setDrop(found)
    if (found?.handover_qr_token && found.handover_qr_expires_at) {
      setToken(found.handover_qr_token)
      setExpiresAt(found.handover_qr_expires_at)
    } else {
      setToken('')
      setExpiresAt('')
    }
  }, [transferId])

  useEffect(() => {
    void loadDrop()
  }, [loadDrop])

  useEffect(() => {
    if (!drop || drop.status === 'receiving_completed') return undefined
    const timer = window.setInterval(() => void loadDrop(), 3_000)
    return () => window.clearInterval(timer)
  }, [drop?.status, loadDrop])

  useEffect(() => {
    if (!drop || drop.status === 'qr_shown' || drop.status === 'pending') return
    setToken('')
    setExpiresAt('')
  }, [drop?.status])

  async function showQr(force = false) {
    if (!device || !transferId) return
    setLoading(true)
    try {
      const res = await generateKitchenTransferQr(transferId, device.deviceId, { force })
      setToken(res.token)
      setExpiresAt(res.expires_at)
      await loadDrop()
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Gagal buat QR')
    } finally {
      setLoading(false)
    }
  }

  const isReceived = drop?.status === 'receiving_completed'
  const isScanned = drop?.status === 'receiving_draft'
  const canShowQr = !isReceived && !isScanned
  const expired = isKitchenTransferQrExpired(expiresAt)
  const ttlLabel = qrCountdownLabel(
    expiresAt,
    now,
    delmanTerms.handover.ttlExpired,
    delmanTerms.handover.ttlRemaining,
  )
  const lineSummary = drop ? formatTransferLineSummary(drop.lines) : ''
  const qrPayload = token ? buildKitchenTransferHandoverQrPayload(transferId, token) : ''

  return (
    <section className="space-y-4">
      <DelmanPageHeader title="QR dapur tujuan" backHref="/delman/kitchen-transfer" />
      {drop && (
        <div className="card space-y-2">
          <p className="text-headline-md text-primary">→ {drop.target_kitchen_name}</p>
          <p className="text-body-sm text-on-surface-variant">
            Dari {drop.source_kitchen_name} · {lineSummary}
          </p>
          <ul className="text-body-sm text-on-surface-variant space-y-1 border-t border-surface-variant pt-2">
            {drop.lines.map((line: ApiKitchenTransferLine, i: number) => (
              <li key={`${line.item_type}-${line.product_name ?? line.material_name}-${i}`}>
                {formatTransferLineLabel(line)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isReceived && (
        <div className="card bg-primary-container/30 border border-primary/20">
          <p className="text-body-sm font-semibold text-primary">{delmanTerms.kitchenTransfer.handoverDone}</p>
        </div>
      )}
      {isScanned && (
        <div className="card bg-warning-container/30 border border-warning/20">
          <p className="text-body-sm font-semibold text-on-surface">{delmanTerms.kitchenTransfer.handoverScanned}</p>
        </div>
      )}
      {canShowQr && token ? (
        <div className="card flex flex-col items-center gap-3">
          <QrDisplay token={qrPayload} size="large" />
          <p className={`text-body-sm ${expired ? 'text-error' : 'text-on-surface-variant'}`}>
            {expired ? 'QR kedaluwarsa' : ttlLabel}
          </p>
          <p className="text-label-md font-mono text-on-surface-variant break-all text-center">{qrPayload}</p>
        </div>
      ) : canShowQr ? (
        <p className="text-body-sm text-on-surface-variant">{delmanTerms.kitchenTransfer.handoverHint}</p>
      ) : null}
      {canShowQr && (!token || expired) && (
        <button
          type="button"
          className="btn-primary w-full min-h-[44px]"
          disabled={loading}
          onClick={() => void showQr(expired)}
        >
          {!token ? 'Tampilkan QR' : 'Perbarui QR'}
        </button>
      )}
      {toast && <Toast message={toast} tone="info" onDismiss={() => setToast(null)} />}
    </section>
  )
}
